
import { useEffect, useState } from "react";
import { useCongregations } from "./sermon-plans/use-congregations";
import { useSermonPlansCrud } from "./sermon-plans/use-sermon-plans-crud";
import { useSermonPlansFetch } from "./sermon-plans/use-sermon-plans-fetch";
import { useAutoGenerationDate } from "./sermon-plans/use-auto-generation-date";
import { useAIGeneration } from "./sermon-plans/use-ai-generation";
import { SermonPlan, Congregation, SermonFormData } from "./sermon-plans/types";

export type { SermonPlan, Congregation, SermonFormData };

export function useSermonPlans() {
  const [loading, setLoading] = useState(true);
  const [lastFetchAttempt, setLastFetchAttempt] = useState(0);
  
  const { congregations, setCongregations, fetchCongregations, isOffline: congregationsOffline } = useCongregations();
  const { 
    sermonPlans, 
    setSermonPlans, 
    fetchSermonPlans, 
    loading: fetchLoading, 
    setLoading: setFetchLoading, 
    error: fetchError,
    isOffline: sermonPlansOffline 
  } = useSermonPlansFetch();
  
  const { createSermonPlan, updateSermonPlan, deleteSermonPlan } = useSermonPlansCrud();
  const { nextAutoGenerationDate } = useAutoGenerationDate();
  const { runAutoGeneration: runAIGeneration, isGenerating } = useAIGeneration();

  const isOffline = congregationsOffline || sermonPlansOffline;

  const fetchData = async () => {
    // Prevent multiple fetches in quick succession
    const now = Date.now();
    if (now - lastFetchAttempt < 2000) {
      return;
    }
    
    setLastFetchAttempt(now);
    setLoading(true);
    
    try {
      // Fetch congregations first
      await fetchCongregations();
      // Then fetch sermon plans
      await fetchSermonPlans();
    } catch (error) {
      console.error("Error in fetchData:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSermonPlan = async (sermonPlanData: Partial<SermonPlan> & SermonFormData) => {
    setLoading(true);
    const success = await createSermonPlan(sermonPlanData);
    if (success) {
      await fetchData();
    } else {
      setLoading(false);
    }
    return success;
  };

  const handleUpdateSermonPlan = async (id: string, sermonPlanData: Partial<SermonPlan> & SermonFormData) => {
    setLoading(true);
    const success = await updateSermonPlan(id, sermonPlanData);
    if (success) {
      await fetchData();
    } else {
      setLoading(false);
    }
    return success;
  };

  const handleDeleteSermonPlan = async (id: string) => {
    setLoading(true);
    const success = await deleteSermonPlan(id);
    if (success) {
      await fetchData();
    } else {
      setLoading(false);
    }
    return success;
  };

  const runAutoGeneration = () => {
    setLoading(true);
    runAIGeneration();
    // Refresh data after simulated processing
    setTimeout(() => {
      fetchData();
    }, 2000);
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Sync loading state with fetch loading
  useEffect(() => {
    setLoading(fetchLoading || isGenerating);
  }, [fetchLoading, isGenerating]);
  
  // Try to refetch when coming back online
  useEffect(() => {
    if (!isOffline) {
      fetchData();
    }
  }, [isOffline]);

  return {
    sermonPlans,
    congregations,
    loading,
    isGenerating,
    nextAutoGenerationDate,
    isOffline,
    fetchData,
    createSermonPlan: handleCreateSermonPlan,
    updateSermonPlan: handleUpdateSermonPlan,
    deleteSermonPlan: handleDeleteSermonPlan,
    runAutoGeneration
  };
}
